import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { Trash } from '@xsolla/ui-icons';

import { useCurrentProject } from '../../../contexts/settings';
import { ValidationRule } from '../..';
import { FieldBlock, FieldSelect, FieldSwitch, Separator } from '../../ui';
import { ValidationFieldName } from '../ValidationRule/utils';
import { ExtraInputButton } from './style';

type PasswordConfigurationProps = {
  deleteUrl: () => void;
  projectId: string;
};

export const PasswordConfiguration = ({
  deleteUrl,
  projectId
}: PasswordConfigurationProps): JSX.Element => {
  const intl = useIntl();
  const { currentProject } = useCurrentProject(projectId);

  const selectCallbackUrls = useMemo(() => {
    return currentProject?.login_urls.map((callbackUrlString) => ({
      children: callbackUrlString,
      value: callbackUrlString,
      'data-test-id': `callbackUrl-${callbackUrlString}`
    }));
  }, [currentProject]);

  return (
    <>
      <FieldBlock dataTestId="password-validation__validation-rules">
        <ValidationRule name={ValidationFieldName.PASSWORD_FIELD} />
      </FieldBlock>
      <Separator />
      <FieldBlock
        title={intl.formatMessage({ id: 'configuration-page.password.miscellaneous.title' })}
        description={intl.formatMessage({
          id: 'configuration-page.password.miscellaneous.description'
        })}
        notification={intl.formatMessage({
          id: 'configuration-page.password.miscellaneous.notification'
        })}>
        <FieldSwitch
          name="password_reset_allowed"
          caption={intl.formatMessage({
            id: 'configuration-page.password.miscellaneous.recover-pass.description'
          })}
          checked={currentProject?.password_reset_allowed}
          label={intl.formatMessage({
            id: 'configuration-page.password.miscellaneous.recover-pass.title'
          })}
        />

        <FieldSwitch
          name="password_history"
          caption={intl.formatMessage({
            id: 'configuration-page.password.miscellaneous.pass-history.description'
          })}
          checked={false}
          label={intl.formatMessage({
            id: 'configuration-page.password.miscellaneous.pass-history.title'
          })}
          disabled
        />

        <FieldSwitch
          name="password_repeat"
          caption={intl.formatMessage({
            id: 'configuration-page.password.miscellaneous.pass-check.description'
          })}
          checked={false}
          label={intl.formatMessage({
            id: 'configuration-page.password.miscellaneous.pass-check.title'
          })}
          disabled
        />
      </FieldBlock>
      <Separator />
      <FieldBlock
        title={intl.formatMessage({
          id: 'configuration-page.password.password-reset-cb-url.title'
        })}
        description={intl.formatMessage({
          id: 'configuration-page.password.password-reset-cb-url.description'
        })}>
        <FieldSelect
          options={selectCallbackUrls || []}
          name="password_reset_callback_url"
          extraRight={
            // eslint-disable-next-line react/jsx-wrap-multilines
            <ExtraInputButton palette="muted" onClick={deleteUrl} data-test-id="-delete">
              <Trash />
            </ExtraInputButton>
          }
        />
      </FieldBlock>
    </>
  );
};
